/**
 * useSectorName Hook
 * 
 * Provides localized sector names using i18n translations
 * Falls back to database name_vi or sector_name if translation missing
 */

import { useTranslation } from 'react-i18next';

/**
 * Hook to get localized sector names
 * 
 * @returns {Object} - { getSectorName, normalizeSectorCode }
 */
export const useSectorName = () => {
  const { t, i18n } = useTranslation();
  
  /**
   * Normalize sector code for use as translation key
   * Converts: "BANK - RETAIL/CONSUMER" → "BANK_RETAIL_CONSUMER"
   * 
   * @param {string} code - Raw sector code from database
   * @returns {string} - Normalized key for translations
   */
  const normalizeSectorCode = (code) => {
    if (!code) return '';
    
    return code
      .replace(/\s+/g, '_')        // Spaces to underscores
      .replace(/\//g, '_')         // Slashes to underscores
      .replace(/&/g, 'AND')        // & to AND
      .replace(/[()]/g, '')        // Remove parentheses
      .replace(/-/g, '')           // Remove hyphens
      .replace(/_+/g, '_')         // Collapse multiple underscores
      .toUpperCase();
  };
  
  /**
   * Get localized sector name
   * 
   * Priority:
   * 1. If Vietnamese: Use database name_vi (direct from DB)
   * 2. Try i18n translation key (sectors.NORMALIZED_CODE)
   * 3. Fallback to sector_name (English from DB)
   * 
   * @param {Object} sector - Sector object with fields:
   *   - sector_code: string (e.g., "BANK_RETAIL/CONSUMER")
   *   - sector_name: string (e.g., "Bank - Retail/Consumer")
   *   - name_vi: string (e.g., "Ngân Hàng - Bán Lẻ / Cá Nhân")
   * 
   * @returns {string} - Localized sector name
   * 
   * @example
   * const { getSectorName } = useSectorName();
   * 
   * const sector = {
   *   sector_code: "BANK_RETAIL/CONSUMER",
   *   sector_name: "Bank - Retail/Consumer",
   *   name_vi: "Ngân Hàng - Bán Lẻ / Cá Nhân"
   * };
   * 
   * // In Vietnamese: returns "Ngân Hàng - Bán Lẻ / Cá Nhân"
   * // In English: returns "Bank - Retail/Consumer"
   * const displayName = getSectorName(sector);
   */
  const getSectorName = (sector) => {
    if (!sector) return '';
    
    // Priority 1: If Vietnamese and name_vi exists, use it directly
    if (i18n.language === 'vi' && sector.name_vi) {
      return sector.name_vi;
    }
    
    // Priority 2: Try to get translation from i18n files
    if (sector.sector_code) {
      const normalizedCode = normalizeSectorCode(sector.sector_code);
      const translationKey = `sectors.${normalizedCode}`;
      const translated = t(translationKey);
      
      // If translation exists (not equal to key), use it
      if (translated !== translationKey) {
        return translated;
      }
    }
    
    // Priority 3: Fallback to sector_name (English from database)
    return sector.sector_name || '';
  };
  
  return { 
    getSectorName,
    normalizeSectorCode 
  };
};

